import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { LoginService } from './login.service';
import { SecureStorageService } from '../modules/core/services/secure-storage.service';
import { LocalStorageRelatedData } from '../local-storage-related-data.model';
import { ActualURLPaths, FeaturesPaths } from '../models/routes-constants';
import { SideNavService } from '../components/nav-bar/Services/side-nav.service';

export interface AuthChangeEvent {
    type: 'login' | 'logout';
    source: 'local' | 'remote'; // 'remote' means from another tab
}

export interface AuthSyncOptions {
    onLogout: 'navigate' | 'reload';
    onLogin?: 'navigate' | 'reload' | 'none';
}

/**
 * AuthSyncService
 *
 * Handles multi-tab authentication synchronization with proper cleanup.
 * When a user logs in or out in one tab, this service ensures other tabs
 * react appropriately (navigate to login, reload, etc.).
 */
@Injectable({ providedIn: 'root' })
export class AuthSyncService implements OnDestroy {
    private storageHandler: ((event: StorageEvent) => void) | null = null;
    private currentOptions: AuthSyncOptions | null = null;
    private destroy$ = new Subject<void>();

    /**
     * Observable that emits when authentication state changes.
     * Components can subscribe to react to login/logout events.
     */
    authChange$ = new Subject<AuthChangeEvent>();

    constructor(
        private loginService: LoginService,
        private router: Router,
        private secureStorageService: SecureStorageService,
        private sideNavService: SideNavService
    ) {}

    /**
     * Start listening for authentication changes from other tabs.
     *
     * @param options Configuration for how to handle auth changes
     *   - onLogout: 'navigate' to go to login page, 'reload' to reload the page
     *   - onLogin: 'navigate' to go to dashboard, 'reload' to reload, 'none' to do nothing
     */
    startListening(options: AuthSyncOptions): void {
        // Clean up any existing listener first
        this.stopListening();

        this.currentOptions = options;

        // Create bound handler so we can remove it later
        this.storageHandler = (event: StorageEvent) => {
            this.handleStorageEvent(event);
        };

        window.addEventListener('storage', this.storageHandler);
    }

    /**
     * Stop listening for authentication changes.
     * Should be called when component is destroyed.
     */
    stopListening(): void {
        if (this.storageHandler) {
            window.removeEventListener('storage', this.storageHandler);
            this.storageHandler = null;
        }
        this.currentOptions = null;
    }

    /**
     * Clear authentication flags from storage.
     * Should be called on app startup to prevent stale flags from triggering events.
     */
    clearAuthFlags(): void {
        this.secureStorageService.removeItem(LocalStorageRelatedData.LOGGED_OUT);
        this.secureStorageService.removeItem(LocalStorageRelatedData.LOGGED_IN);
    }

    /**
     * Initialize session storage counter.
     * Used by other modules to track page reloads.
     */
    initializeSessionStorage(): void {
        this.secureStorageService.setSessionItem(LocalStorageRelatedData.SessionStorage.RC, '0');
    }

    private handleStorageEvent(event: StorageEvent): void {
        if (!this.currentOptions) {
            return;
        }

        // Handle login event from another tab
        if (event.key === LocalStorageRelatedData.LOGGED_IN && event.newValue === 'true') {
            this.handleRemoteLogin();
        }
        // Handle logout event from another tab
        else if (event.key === LocalStorageRelatedData.LOGGED_OUT && event.newValue === 'true') {
            this.handleRemoteLogout();
        }
    }

    private handleRemoteLogin(): void {
        if (!this.currentOptions) {
            return;
        }

        // Emit auth change event
        this.authChange$.next({ type: 'login', source: 'remote' });

        // Notify login service
        this.loginService.userLogInOrLogoutEventEmitter(true);

        const onLogin = this.currentOptions.onLogin || 'navigate';

        if (onLogin === 'reload') {
            window.location.reload();
        } else if (onLogin === 'navigate') {
            this.router.navigate([FeaturesPaths.DASHBOARD], {
                queryParams: { nc: new Date().getTime() }
            }).catch((error) => {
                console.error('Navigation failed:', error);
                window.location.reload();
            }).then(() => {
                window.location.reload();
            });
        }
        // If 'none', do nothing
    }

    private handleRemoteLogout(): void {
        if (!this.currentOptions) {
            return;
        }

        // Emit auth change event
        this.authChange$.next({ type: 'logout', source: 'remote' });

        // Close sidenav
        this.sideNavService.close();

        // Notify login service
        this.loginService.userLogInOrLogoutEventEmitter(false);

        if (this.currentOptions.onLogout === 'reload') {
            window.location.reload();
        } else {
            // Navigate to login page
            this.router.navigate([ActualURLPaths.LOGIN], {
                queryParams: { nc: new Date().getTime() }
            }).catch((error) => {
                console.error('Navigation failed:', error);
                window.location.reload();
            }).then(() => {
                window.location.reload();
            });
        }
    }

    ngOnDestroy(): void {
        this.stopListening();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
